import { useRef, useState, useEffect } from 'react';

import { Box, Button, HStack, useOutsideClick, Textarea, Paragraph } from '@vimeo/bokeh';
import { translate } from '@vimeo/i18n-runtime';

import { T } from '../../translations';
import { CommentsFooterType, RichTextAreaRefType } from '../../types';

export const InputArea = ({
  errorMessage = '',
  isPublic,
  onSubmit,
  editingText = '',
  autoFocus = false,
  onCancel,
  shouldShowCancel = false,
  submitButtonText = T.SubmitComment,
  isLoading = false,
  richCommentComponent: RichCommentComponent,
  commentContextFields,
  setDraftText,
  isGuestModalActive = false,
  isDisabled = false,
}: CommentsFooterType) => {
  const inputAreaRef = useRef<HTMLDivElement>(null);
  const richtextAreaRef = useRef<RichTextAreaRefType>({} as RichTextAreaRefType);

  const [textAreaExpanded, setTextAreaExpanded] = useState(false);
  const [commentText, setCommentText] = useState(editingText);

  const onFocus = () => {
    setTextAreaExpanded(true);
    if (!!RichCommentComponent) {
      richtextAreaRef?.current?.focusEnd();
    }
  };

  const onSaveClick = () => {
    richtextAreaRef?.current?.onSubmit?.();
    onSubmit?.(commentText);
    setDraftText?.('');
  };
  useEffect(() => {
    if (isLoading) {
      setCommentText('');
    }
    if (!isLoading && !editingText && !errorMessage) {
      richtextAreaRef?.current?.clearContent?.();
      setTextAreaExpanded(false);
      richtextAreaRef?.current?.removeFocus?.();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLoading, errorMessage]); // We only want to reset the text when isLoading changes, and dont want commentText to be a dependency

  const onCancelClick = () => {
    setDraftText?.(shouldShowCancel ? '' : commentText);
    setTextAreaExpanded(false);
    onCancel?.();
  };

  const onKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter') {
      if (!e.shiftKey && commentText.trim()) {
        e.preventDefault();
        onSaveClick();
        (e.target as HTMLTextAreaElement).blur();
      } else if (!e.shiftKey) {
        e.preventDefault(); // Prevent newline
      }
    }
  };

  useOutsideClick({
    enabled: !isGuestModalActive,
    ref: inputAreaRef,
    handler: onCancelClick,
  });

  return (
    <Box w="100%" ref={inputAreaRef}>
      {RichCommentComponent ? (
        <RichCommentComponent
          autoFocus={autoFocus}
          richtext={commentText}
          isExpanded={textAreaExpanded}
          isPublic={isPublic}
          placeholder={T.InputPlaceholder}
          setCommentText={(text: string) => setCommentText(text)}
          onFocus={onFocus}
          richtextAreaRef={richtextAreaRef}
          commentContextFields={commentContextFields}
          onSubmitComment={onSubmit}
          isPostCommentLoading={isLoading}
          isDisabled={isDisabled}
        />
      ) : (
        // this is only rendered on comments widget default case + comments-ui-test case where RichCommentComponent is not provided
        <Textarea
          w="100%"
          mb={0}
          size="sm"
          rows={textAreaExpanded ? 3 : 1}
          onFocus={onFocus}
          placeholder={T.InputPlaceholder}
          value={commentText}
          onChange={(e) => setCommentText(e.target.value)}
          autoFocus={autoFocus}
          resize="none"
          isInvalid={!!errorMessage}
          onKeyDown={onKeyDown}
        />
      )}
      {textAreaExpanded ? (
        <HStack gap="100" justifyContent="space-between" alignItems="center">
          {!!errorMessage ? (
            <Paragraph size="sm" color="status-destructive-primary" py={8}>
              {errorMessage}
            </Paragraph>
          ) : (
            <Box fontSize={12} opacity={0.6} py={8} color="text-tertiary">
              {!editingText ? translate('Use @ to mention others') : null}
            </Box>
          )}

          <HStack>
            {shouldShowCancel ? (
              <Button variant="minimal" size="sm" onClick={onCancelClick}>
                {T.Cancel}
              </Button>
            ) : null}
            <Button
              variant="primary"
              type="submit"
              isLoading={isLoading}
              size="sm"
              zIndex={1}
              onClick={onSaveClick}
              isDisabled={
                !!RichCommentComponent
                  ? !richtextAreaRef?.current?.commentLength()
                  : !commentText.trim()
              }
            >
              <span>{submitButtonText}</span>
            </Button>
          </HStack>
        </HStack>
      ) : null}
    </Box>
  );
};
